import React, { useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import GroupAddIcon from "@mui/icons-material/GroupAdd";

import CustomTooltip from "components/common/Tooltip";

import { hasPermission } from "hooks/usePermission";

import "./GroupTree.scss";

function GroupNode({ node, depth, onAction }) {
  const [open, setOpen] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const employeeCount = node.employeeCount ?? 0;
  const nestedEmployeeCount = node.nestedEmployeeCount ?? 0;
  const customerUserCount = node.customerUserCount ?? 0;
  const nestedCustomerUserCount = node.nestedCustomerUserCount ?? 0;

  const canCreate = hasPermission("groups.create");
  const canRename = hasPermission("groups.rename");
  const canMove = hasPermission("groups.move");
  const canDelete = hasPermission("groups.delete");
  const canAssignMember = hasPermission("groups.assign_member");

  return (
    <>
      <div className="group-tree__row" style={{ "--group-depth": depth }}>
        <IconButton
          size="small"
          className={`group-tree__toggle ${hasChildren ? "" : "group-tree__toggle--hidden"}`}
          onClick={() => setOpen(!open)}
        >
          {open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </IconButton>

        <div className="group-tree__label">
          <span className="group-tree__name-line">
            <span className="para-body-m-medium color-text-heading-primary">{node.Name}</span>
            <span className="group-tree__count para-body-s-regular">
              <span>(</span>
              <span>{`${employeeCount} own ${employeeCount === 1 ? "employee" : "employees"}, ${nestedEmployeeCount} other`}</span>
              <span className="group-tree__count-divider">·</span>
              <span>{`${customerUserCount} own ${customerUserCount === 1 ? "user" : "users"}, ${nestedCustomerUserCount} other`}</span>
              <span>)</span>
            </span>
          </span>
          {node.Is_Main && (
            <div className="group-tree__meta">
              <span className="para-body-s-regular color-text-heading-secondary">Main group</span>
            </div>
          )}
        </div>

        <div className="group-tree__actions">
          {canCreate && (
            <CustomTooltip content="Add sub-group">
              <IconButton
                size="small"
                aria-label="Add sub-group"
                className="group-tree__action"
                onClick={() => onAction("create", node)}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </CustomTooltip>
          )}
          {!node.Is_Main && (
            <>
              {canRename && (
                <CustomTooltip content="Rename">
                  <IconButton
                    size="small"
                    aria-label="Rename"
                    className="group-tree__action"
                    onClick={() => onAction("rename", node)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </CustomTooltip>
              )}
              {canMove && (
                <CustomTooltip content="Move">
                  <IconButton
                    size="small"
                    aria-label="Move"
                    className="group-tree__action"
                    onClick={() => onAction("move", node)}
                  >
                    <DriveFileMoveIcon fontSize="small" />
                  </IconButton>
                </CustomTooltip>
              )}
              {canDelete && (
                <CustomTooltip content="Delete">
                  <IconButton
                    size="small"
                    aria-label="Delete"
                    className="group-tree__action"
                    onClick={() => onAction("delete", node)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CustomTooltip>
              )}
            </>
          )}
          {canAssignMember && (
            <CustomTooltip content="Assign member">
              <IconButton
                size="small"
                aria-label="Assign member"
                className="group-tree__action"
                onClick={() => onAction("assign", node)}
              >
                <GroupAddIcon fontSize="small" />
              </IconButton>
            </CustomTooltip>
          )}
        </div>
      </div>

      <Collapse in={open} timeout="auto" unmountOnExit>
        {hasChildren && (
          <div className="group-tree__children">
            {node.children.map((child) => (
              <GroupNode key={child.Group_ID} node={child} depth={depth + 1} onAction={onAction} />
            ))}
          </div>
        )}
      </Collapse>
    </>
  );
}

GroupNode.propTypes = {
  node: PropTypes.shape({
    Group_ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Name: PropTypes.string,
    Is_Main: PropTypes.bool,
    employeeCount: PropTypes.number,
    nestedEmployeeCount: PropTypes.number,
    customerUserCount: PropTypes.number,
    nestedCustomerUserCount: PropTypes.number,
    children: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  depth: PropTypes.number.isRequired,
  onAction: PropTypes.func.isRequired,
};

export default function GroupTree({ tree, onAction }) {
  return (
    <div className="group-tree">
      {tree.map((node) => (
        <GroupNode key={node.Group_ID} node={node} depth={0} onAction={onAction} />
      ))}
    </div>
  );
}

GroupTree.propTypes = {
  tree: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAction: PropTypes.func.isRequired,
};
